import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@scott/ui/client";
import type * as React from "react";
import { forwardRef } from "react";
import { IconXMark } from "../../icons";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: React.RefObject<React.ElementRef<
    typeof DialogPrimitive.Overlay
  > | null>;
}) => (
  <DialogPrimitive.Overlay
    className={cn(
      "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=open]:animate-in",
      "fixed inset-0 z-[70] bg-black/80",
      className
    )}
    ref={ref}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

function resetBodyPointerEventsIfNoDialogOpen() {
  const hasOpenDialog = document.querySelector(
    '[role="dialog"][data-state="open"]'
  );
  if (!hasOpenDialog) {
    document.body.style.pointerEvents = "";
  }
}

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  imageDialog?: boolean;
  imageController?: React.ReactNode;
  /** Full-screen dark overlay for gallery/lightbox use cases. Renders children directly without wrapper. */
  galleryOverlay?: boolean;
};

// biome-ignore lint/nursery/noReactForwardRef: -
const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      imageDialog,
      imageController,
      galleryOverlay,
      onCloseAutoFocus,
      ...props
    },
    ref
  ) => {
    const handleCloseAutoFocus = (event: Event) => {
      onCloseAutoFocus?.(event);
      resetBodyPointerEventsIfNoDialogOpen();
    };

    // Gallery overlay variant - full-screen dark overlay with high z-index
    if (galleryOverlay) {
      return (
        <DialogPortal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[9999] bg-[#3F3F46]" />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className={cn(
              "fixed inset-0 z-[9999] overflow-y-auto focus:outline-none",
              className
            )}
            onCloseAutoFocus={handleCloseAutoFocus}
            ref={ref}
            {...props}
          >
            {children}
          </DialogPrimitive.Content>
        </DialogPortal>
      );
    }

    // Image dialog variant
    if (imageDialog) {
      return (
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content
            className={cn(
              "fixed inset-0 z-[80] flex h-full w-full items-center justify-center bg-transparent",
              className
            )}
            onCloseAutoFocus={handleCloseAutoFocus}
            ref={ref}
            {...props}
          >
            <DialogPrimitive.Close className="fixed top-4 right-4 z-[90] flex h-9 w-9 cursor-pointer items-center justify-center rounded-sm border border-input bg-black/60 p-2 text-white opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none md:bg-transparent">
              <IconXMark size="sm" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
            {imageController && (
              <div className="fixed right-4 bottom-4 z-[90]">
                {imageController}
              </div>
            )}
            <div className="flex h-full w-full items-center justify-center">
              <div className="h-full w-full rounded-lg bg-white p-2 md:h-[90vh] md:w-[90vh] md:max-w-[90vh]">
                {children}
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPortal>
      );
    }

    // Default dialog variant
    return (
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          className={cn(
            "-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-[80] grid w-full max-w-lg gap-4 border bg-background p-6 shadow-lg duration-200 sm:rounded-lg",
            "data-[state=open]:zoom-in-95 data-[state=open]:fade-in-0 data-[state=open]:animate-in",
            "data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out",
            className
          )}
          onCloseAutoFocus={handleCloseAutoFocus}
          ref={ref}
          {...props}
        >
          {children}
          <DialogPrimitive.Close className="absolute top-6 right-4 h-8 w-8 rounded-sm p-2 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <IconXMark size="sm" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: React.RefObject<React.ElementRef<typeof DialogPrimitive.Title> | null>;
}) => (
  <DialogPrimitive.Title
    className={cn(
      "font-semibold text-lg leading-none tracking-tight",
      className
    )}
    ref={ref}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = ({
  className,
  ref,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: React.RefObject<React.ElementRef<
    typeof DialogPrimitive.Description
  > | null>;
}) => (
  <DialogPrimitive.Description
    className={cn("text-muted-foreground text-sm", className)}
    ref={ref}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
